import React from 'react';
import { Box, Theme, useMediaQuery } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import palette from '@ui/palette';
import { ActorsProps } from '@avatar/components/ActorsList';
import type { Actor } from '@avatar/types';
import {
  Project,
  ProjectAccessRoleEnum,
  StudioProjectVersion,
} from '../../../../server/graphql/__generated.types';
import useEditMode from './useEditMode';
import { useAppSelector } from '../../../store/hooks';
import StudioScriptOptions from './StudioScriptOptions';
import StudioCuesOptions from './StudioCuesOptions';
import { RIGHT_RAIL_WIDTH } from '../../BaseLayout/components/RightRail';
import useActiveActor from '../../avatar/hooks/useActiveActor';
import {
  Actions,
  Subjects,
  useUserAbilitiesContext,
} from '../../../context/user/UserContext';
import StudioClipComments from './StudioClipComments';
import useEvaluateStudioProjectVersionFeatures from '../useEvaluateStudioProjectVersionFeatures';
import StudioToolbarViewOnly from './StudioToolbarViewOnly';

export const ID_STUDIO_DRAWER_LEFT = 'studio-drawer-left';
export const ID_STUDIO_DRAWER_RIGHT = 'studio-drawer-right';

const DESKTOP_ACTOR_LIMIT = 4;

interface StudioToolbarWrapperProps {
  projectScopedActors: Actor[];
  project: Project;
  tags?: ActorsProps['tags'];
}

const useDrawerStyles = makeStyles()(() => ({
  container: {
    backgroundColor: palette.gray_1100,
    width: RIGHT_RAIL_WIDTH,
    height: '100%',
  },
}));

export const StudioToolbarWrapper = (props: StudioToolbarWrapperProps) => {
  const { project, tags, projectScopedActors } = props;
  const { classes, cx } = useDrawerStyles();
  const editorState = useAppSelector(appState => appState.editor);
  const commentsState = useAppSelector(appState => appState.comments);
  const ability = useUserAbilitiesContext();

  const canAccessCuesMode = project?.studioVersion === StudioProjectVersion.V2;
  const { activeActor, setActiveActor } = useActiveActor(
    projectScopedActors[0],
    project.studioVersion
  );
  const { isScriptMode, isCuesMode } = useEditMode({
    initialEdit: editorState.editMode,
    canAccessCuesMode,
  });
  const isDesktop = useMediaQuery<Theme>(theme => theme.breakpoints.up('md'));

  const { activeCueGroup } = useAppSelector(state => state.cues);

  const projectVersionFeatures =
    useEvaluateStudioProjectVersionFeatures(project);

  const isReadOnlyAccess =
    project.projectAccess.viewerAccessRole?.id ===
    ProjectAccessRoleEnum.ReadOnly;
  const showComments = commentsState.showSidebar;

  const commentsModeView =
    showComments &&
    (ability.can(Actions.UseFeature, Subjects.Comments) ||
      ability.can(Actions.Read, Subjects.Comments)) &&
    !isCuesMode;

  const noAccessCommentOnlyView =
    !project.projectAccess.viewerAccessRole &&
    (ability.can(Actions.UseFeature, Subjects.Comments) ||
      ability.can(Actions.Read, Subjects.Comments)) &&
    !isCuesMode;

  const viewOnly =
    (isReadOnlyAccess && !commentsModeView) || noAccessCommentOnlyView;

  const scriptModeView =
    !isReadOnlyAccess &&
    isScriptMode &&
    !commentsModeView &&
    !noAccessCommentOnlyView;
  const cuesModeView = !isReadOnlyAccess && isCuesMode;

  return (
    <Box
      role="complementary"
      aria-label="configuration drawer"
      className={cx({
        [classes.container]: isScriptMode,
      })}
      id={ID_STUDIO_DRAWER_RIGHT}
    >
      {scriptModeView && (
        <StudioScriptOptions
          project={project}
          actor={activeActor.actor}
          actorVariant={activeActor.variant}
          isPhoneticLibraryEnabled
          setActiveActor={setActiveActor}
          isDesktop={isDesktop}
          desktopActorLimit={DESKTOP_ACTOR_LIMIT}
          tags={tags}
          replacementsDisabled={projectVersionFeatures.replacementsDisabled}
        />
      )}
      {cuesModeView && <StudioCuesOptions value={activeCueGroup} />}
      {commentsModeView && <StudioClipComments projectId={project.id} />}
      {viewOnly && <StudioToolbarViewOnly />}
    </Box>
  );
};

export default StudioToolbarWrapper;
